<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { brand } from '../config/brand'

const menuOpen = ref(false)
const productOpen = ref(false)

const activeKey = computed(() => {
  if (typeof window === 'undefined') return 'home'
  const p = window.location.pathname || '/'
  if (p === '/' || p.endsWith('/index.html')) return 'home'
  if (/\/[a-z0-9-]+-app\/?$/.test(p)) return 'product'
  if (p === '/price' || p === '/price/' || p.endsWith('/price.html')) return 'pricing'
  if (p.endsWith('/remove-watermark-api.html') || p.endsWith('/remove-watermark-api/')) return 'api'
  return 'home'
})

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function toggleProduct() {
  productOpen.value = !productOpen.value
}

function closeAll() {
  menuOpen.value = false
  productOpen.value = false
}

function onDocClick(e: MouseEvent) {
  const t = e.target as Node | null
  if (!t) return
  const nav = document.querySelector('.whNav')
  if (nav && nav.contains(t)) return
  closeAll()
}

onMounted(() => {
  window.addEventListener('click', onDocClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', onDocClick)
})
</script>

<template>
  <header class="whNav">
    <div class="whContainer whNavInner">
      <a class="whBrand" href="/">
        <span class="whLogo" aria-hidden="true">
          <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lg-nav" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#5B23FF" />
                <stop offset="1" stop-color="#008BFF" />
              </linearGradient>
            </defs>
            <rect x="16" y="16" width="480" height="480" rx="120" fill="url(#lg-nav)" />
            <g transform="rotate(-45 256 256)">
              <path d="M224,133 L288,133 A32,32 0 0 1 320,165 L320,287 L192,287 L192,165 A32,32 0 0 1 224,133 Z" fill="#FFFFFF" />
              <path d="M192,292 L320,292 L320,347 A32,32 0 0 1 288,379 L224,379 A32,32 0 0 1 192,347 Z" fill="#FFFFFF" />
            </g>
            <path d="M400,48 Q400,112 464,112 Q400,112 400,176 Q400,112 336,112 Q400,112 400,48 Z" fill="#E4FF30" />
          </svg>
        </span>
        <span class="whBrandText">
          <span class="whBrandName">{{ brand.productName }}</span>
          <span class="whBrandSlogan">{{ brand.slogan }}</span>
        </span>
      </a>

      <nav :class="['whNavLinks', menuOpen ? 'open' : '']" aria-label="Top Navigation">
        <a href="/" :class="activeKey === 'home' ? 'nav-active' : ''">首页</a>
        <div :class="['whNavItem', productOpen ? 'open' : '']">
          <button :class="['whNavLinkDrop', activeKey === 'product' ? 'nav-active' : '']" type="button" aria-haspopup="true" @click.stop="toggleProduct">
            产品
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div class="whDropdown">
            <a class="whDropItem" href="/video-watermark-removal-app/">
              <span class="whDropLogo" style="background:rgba(0, 0, 0, 0.18);">
                <img src="/assets/ai-tool-icons/video-watermark-removal.svg" alt="视频去水印" width="20" height="20" />
              </span>
              <span class="whDropBody"><b>视频去水印</b><span>消除各种样式的文字和图形水印</span></span>
            </a>
            <a class="whDropItem" href="/video-subtitle-removal-app/">
              <span class="whDropLogo" style="background:rgba(0, 0, 0, 0.18);">
                <img src="/assets/ai-tool-icons/video-subtitle-removal.svg" alt="视频去字幕" width="20" height="20" />
              </span>
              <span class="whDropBody"><b>视频去字幕</b><span>消除视频字幕或文字水印</span></span>
            </a>
            <a class="whDropItem" href="/video-object-removal-app/">
              <span class="whDropLogo" style="background:rgba(0, 0, 0, 0.18);">
                <img src="/assets/ai-tool-icons/video-object-removal.svg" alt="万能视频消除" width="20" height="20" />
              </span>
              <span class="whDropBody"><b>万能视频消除</b><span>消除人或物、及马赛克等遮挡</span></span>
            </a>
            <a class="whDropItem" href="/video-enhance-app/">
              <span class="whDropLogo" style="background:rgba(0, 0, 0, 0.18);">
                <img src="/assets/ai-tool-icons/video-enhance.svg" alt="视频增强" width="20" height="20" />
              </span>
              <span class="whDropBody"><b>视频增强</b><span>将普通视频转化为 2K 超清视频</span></span>
            </a>
            <a class="whDropItem" href="/ai-workbench/">
              <span class="whDropLogo" style="background:rgba(0, 0, 0, 0.18);">
                <img src="/assets/ai-tool-icons/ai-workbench.svg" alt="批处理工作台" width="20" height="20" />
              </span>
              <span class="whDropBody"><b>批处理工作台</b><span>批量处理视频去字幕、去水印</span></span>
            </a>
          </div>
        </div>
        <a href="/remove-watermark-api/" :class="activeKey === 'api' ? 'nav-active' : ''">API</a>
        <a href="/price/" :class="activeKey === 'pricing' ? 'nav-active' : ''">定价</a>
      </nav>

      <div class="whNavRight">
        <a class="whNavCta" href="/ai-workbench/">进入工作台</a>
        <button class="whNavToggle" type="button" aria-label="菜单" @click.stop="toggleMenu">☰</button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.whNav {
  --color-highlight: #E4FF30;
  --bg-0: #26203F;
  --bg-1: #362F4F;
  --bg-2: #403765;
  --line: #4A4270;
  --line-strong: #5C5486;
  --text: #F4F2FB;
  --text-2: #C4BEDD;
  --text-3: #8A82AB;
  --primary-glow: rgba(91, 35, 255, 0.55);
  --hl-glow: rgba(228, 255, 48, 0.4);
  --cta-bg: #E4FF30;
  --cta-text: #1E1A33;
  --cta-bg-hover: #F0FF80;
  --radius: 14px;
  --radius-sm: 8px;
  --ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  --shadow-float: 0 24px 60px -22px rgba(10, 8, 24, 0.75);
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "HarmonyOS Sans SC", "Hiragino Sans GB", "Microsoft YaHei", "Segoe UI", sans-serif;
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid var(--line);
  background: rgba(38, 32, 63, 0.8);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.whContainer {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
}

.whNavInner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 78px;
}

.whBrand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.whLogo {
  width: 42px;
  height: 42px;
  flex: none;
  border-radius: 11px;
  box-shadow: 0 4px 18px var(--primary-glow);
  overflow: hidden;
}

.whLogo svg {
  width: 100%;
  height: 100%;
  display: block;
}

.whBrandText {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.whBrandName {
  font-size: 18px;
  font-weight: 800;
  color: var(--text);
  letter-spacing: 0.01em;
}

.whBrandSlogan {
  font-size: 11.5px;
  color: var(--text-3);
  font-weight: 500;
  letter-spacing: 0.02em;
}

.whNavLinks {
  display: flex;
  align-items: center;
  gap: 28px;
}

.whNavLinks a,
.whNavLinkDrop {
  font-size: 14px;
  color: var(--text);
  font-weight: 500;
  transition: color 0.2s var(--ease-out);
}

.whNavLinks a:hover,
.whNavLinkDrop:hover {
  color: var(--color-highlight);
}

.whNavLinks a.nav-active {
  color: var(--color-highlight);
  position: relative;
}

.whNavLinks a.nav-active::after,
.whNavLinkDrop.nav-active::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -10px;
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--color-highlight);
}

.whNavLinkDrop.nav-active {
  color: var(--color-highlight);
  position: relative;
}

.whNavItem {
  position: relative;
}

.whNavLinkDrop {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.whNavLinkDrop svg {
  width: 14px;
  height: 14px;
  transition: transform 0.2s var(--ease-out);
}

.whNavItem:hover .whNavLinkDrop svg {
  transform: rotate(180deg);
}

.whDropdown {
  position: absolute;
  top: calc(100% + 14px);
  left: -24px;
  min-width: 360px;
  background: var(--bg-1);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-float);
  padding: 8px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px);
  transition: opacity 0.22s var(--ease-out), transform 0.22s var(--ease-out), visibility 0.22s;
  z-index: 60;
}

.whNavItem:hover .whDropdown,
.whNavItem.open .whDropdown {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.whDropdown::before {
  content: "";
  position: absolute;
  top: -14px;
  left: 0;
  right: 0;
  height: 14px;
}

.whDropItem {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.2s var(--ease-out);
}

.whDropItem:hover {
  background: var(--bg-2);
}

.whDropLogo {
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: 10px;
  display: grid;
  place-items: center;
}

.whDropLogo svg {
  width: 20px;
  height: 20px;
  color: #fff;
}

.whDropLogo img {
  width: 20px;
  height: 20px;
  display: block;
}

.whDropBody {
  line-height: 1.35;
  min-width: 0;
}

.whDropBody b {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.whDropBody span {
  display: block;
  font-size: 12px;
  color: var(--text-3);
}

.whNavRight {
  display: flex;
  align-items: center;
  gap: 10px;
}

.whNavCta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border-radius: var(--radius-sm);
  background: var(--cta-bg);
  color: var(--cta-text);
  font-size: 14px;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out), background 0.2s var(--ease-out);
}

.whNavCta:hover {
  transform: translateY(-1px);
  background: var(--cta-bg-hover);
  box-shadow: 0 8px 26px var(--hl-glow);
}

.whNavToggle {
  display: none;
  background: none;
  border: 1px solid var(--line-strong);
  color: var(--text);
  border-radius: var(--radius-sm);
  width: 40px;
  height: 40px;
}

@media (max-width: 640px) {
  .whNavLinks {
    display: none;
  }

  .whNavCta {
    display: none;
  }

  .whNavToggle {
    display: grid;
    place-items: center;
  }

  .whNavLinks.open {
    display: flex;
    position: absolute;
    top: 78px;
    left: 0;
    right: 0;
    flex-direction: column;
    gap: 0;
    background: var(--bg-0);
    border-bottom: 1px solid var(--line);
    padding: 12px 24px;
    align-items: flex-start;
  }

  .whNavLinks.open a {
    padding: 12px 0;
    width: 100%;
  }

  .whNavLinks.open a.nav-active::after {
    display: none;
  }

  .whNavLinks.open .whNavLinkDrop {
    padding: 12px 0;
    width: 100%;
    justify-content: flex-start;
  }

  .whNavLinks.open .whDropdown {
    position: static;
    min-width: 0;
    width: 100%;
    opacity: 1;
    visibility: visible;
    transform: none;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
  }

  .whNavLinks.open .whDropdown::before {
    display: none;
  }

  .whNavLinks.open .whDropItem {
    padding: 10px 0;
  }
}
</style>
